import { useMemo, useState } from 'react';
import { MapContainer, Polyline, TileLayer } from 'react-leaflet';
import { Text, useTheme } from '@querodelivery/uni-react';
import { MapPin } from '@/_components/map/map-pin/map-pin';
import { RouteEndpointPin } from '@/_components/map/map-pin/route-endpoint-pin';
import { RouteLine } from '@/_components/map/route-line/route-line';
import { decodeEncodedRoute } from '@/_components/map/route-line/utils';
import { MapController } from '@/_components/map/map-controller/map-controller';
import { MapLegendChips } from '@/_components/map/map-legend-chips/map-legend-chips';
import type { MapLegendTarget } from '@/_components/map/map-legend-chips/types';
import {
  DELIVERY_POINTER_ICON,
  PLACE_POINTER_ICON,
  TILE_LAYER_URL,
} from '@/_components/map/constants';
import {
  getDeliverymanPointerIcon,
  getUniquePositions,
} from '@/_components/map/utils';
import { RouteActionPin } from './components/route-action-pin/route-action-pin';
import { MapOverlayChips } from './components/map-overlay-chips/map-overlay-chips';
import { RouteSequence } from './components/route-sequence/route-sequence';
import {
  DELIVERED_STATUS_TITLE,
  ROUTE_LINE_OPACITY,
  ROUTE_LINE_WEIGHT,
  ROUTE_MAP_HEIGHT,
} from './constants';
import type { DeliveryRouteData, MapPosition, RouteFocusTarget } from './types';
import {
  getDeliveryActionPins,
  getDeliveryRouteSequence,
  getGpsRouteUnavailableMessage,
  getLiveDeliverymanIntentTarget,
  getLiveDeliverymanLocation,
  getRouteEndpoints,
  isDeliveredWithLiveLocation,
  isGpsRouteRetentionExpired,
  numberActionPinsBySequence,
  shouldShowLiveIntentLine,
  toMapPosition,
} from './utils';
import styles from './deliveryman-route-box.module.scss';

interface DeliverymanRouteBoxParams {
  delivery: DeliveryRouteData;
  placeAddress?: string;
  deliveryAddress?: string;
  collapsibleSequence?: boolean;
}

export const DeliverymanRouteBox = ({
  delivery,
  placeAddress,
  deliveryAddress,
  collapsibleSequence = false,
}: DeliverymanRouteBoxParams) => {
  const { color } = useTheme();
  const liveDeliverymanLocation = getLiveDeliverymanLocation(delivery);
  const isDelivered = isDeliveredWithLiveLocation(delivery);
  const [focusedTarget, setFocusedTarget] = useState<RouteFocusTarget>(() =>
    liveDeliverymanLocation ? 'deliveryman' : 'all',
  );
  const [viewRevision, setViewRevision] = useState(0);
  const [popupRevision, setPopupRevision] = useState(0);
  const [sequenceExpandRevision, setSequenceExpandRevision] = useState(0);
  const placeLocation = toMapPosition(delivery.localizacaoOrigem);
  const deliveryLocation = toMapPosition(delivery.localizacaoDestino);
  const intentTarget = getLiveDeliverymanIntentTarget(
    delivery,
    placeLocation,
    deliveryLocation,
  );
  const deliverymanPointerIcon = getDeliverymanPointerIcon(delivery.status);
  const route = useMemo(
    () => decodeEncodedRoute(delivery.encodedRoute),
    [delivery.encodedRoute],
  );
  const { startPosition, endPosition, showStart, showEnd } = useMemo(
    () => getRouteEndpoints(route, placeLocation, deliveryLocation),
    [deliveryLocation, placeLocation, route],
  );
  const rawActionPins = useMemo(
    () => getDeliveryActionPins(delivery),
    [delivery],
  );
  const actionTargets = useMemo(
    () => new Set(rawActionPins.map(pin => pin.id)),
    [rawActionPins],
  );
  const sequence = useMemo(
    () =>
      getDeliveryRouteSequence({
        delivery,
        placeAddress,
        deliveryAddress,
        hasPlace: Boolean(placeLocation),
        hasDelivery: Boolean(deliveryLocation),
        showStart,
        showEnd,
        actionTargets,
      }),
    [
      actionTargets,
      delivery,
      deliveryAddress,
      deliveryLocation,
      placeAddress,
      placeLocation,
      showEnd,
      showStart,
    ],
  );
  const actionPins = useMemo(
    () => numberActionPinsBySequence(rawActionPins, sequence),
    [rawActionPins, sequence],
  );
  const mapPositions = useMemo(
    () =>
      getUniquePositions(
        [
          liveDeliverymanLocation,
          ...route,
          placeLocation,
          deliveryLocation,
          showStart ? startPosition : null,
          showEnd ? endPosition : null,
          ...actionPins.map(pin => pin.position),
        ].filter((position): position is MapPosition => Boolean(position)),
      ),
    [
      actionPins,
      deliveryLocation,
      endPosition,
      liveDeliverymanLocation,
      placeLocation,
      route,
      showEnd,
      showStart,
      startPosition,
    ],
  );
  const showIntentLine = shouldShowLiveIntentLine({
    liveLocation: liveDeliverymanLocation,
    intentTarget,
    hasGpsRoute: route.length > 1,
  });
  const availableTargets = useMemo(() => {
    const targets: MapLegendTarget[] = [];

    if (liveDeliverymanLocation) {
      targets.push('deliveryman');
    }

    if (showStart) {
      targets.push('start');
    }

    if (placeLocation) {
      targets.push('place');
    }

    if (deliveryLocation) {
      targets.push('delivery');
    }

    if (showEnd) {
      targets.push('end');
    }

    return targets;
  }, [
    deliveryLocation,
    liveDeliverymanLocation,
    placeLocation,
    showEnd,
    showStart,
  ]);
  const focusedActionPin = actionPins.find(pin => pin.id === focusedTarget);
  const focusedPosition = useMemo(() => {
    if (focusedActionPin) {
      return focusedActionPin.position;
    }

    if (focusedTarget === 'deliveryman') {
      return liveDeliverymanLocation;
    }

    if (focusedTarget === 'place') {
      return placeLocation;
    }

    if (focusedTarget === 'delivery') {
      return deliveryLocation;
    }

    if (focusedTarget === 'start') {
      return startPosition;
    }

    if (focusedTarget === 'end') {
      return endPosition;
    }

    return null;
  }, [
    deliveryLocation,
    endPosition,
    focusedActionPin,
    focusedTarget,
    liveDeliverymanLocation,
    placeLocation,
    startPosition,
  ]);
  const viewCommand = useMemo(() => {
    if (focusedTarget !== 'all' && focusedPosition) {
      return {
        type: 'fly' as const,
        position: focusedPosition,
        revision: viewRevision,
      };
    }

    return {
      type: 'fit' as const,
      positions: mapPositions,
      revision: viewRevision,
    };
  }, [focusedPosition, focusedTarget, mapPositions, viewRevision]);
  const mapCenter = mapPositions[0] ?? placeLocation ?? deliveryLocation;
  const unavailableMessage = getGpsRouteUnavailableMessage(delivery);
  const isRetentionExpired = isGpsRouteRetentionExpired(delivery.createdAt);

  const handleFocus = (target: RouteFocusTarget) => {
    setFocusedTarget(target);
    setViewRevision(revision => revision + 1);
    setPopupRevision(revision => revision + 1);
  };

  const handlePinClick = (target: RouteFocusTarget) => {
    handleFocus(target);
    setSequenceExpandRevision(revision => revision + 1);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text
          fontSize="md"
          lineHeight="lg"
          fontWeight="semibold"
          tone="supporting"
        >
          Mapa da entrega
        </Text>
      </div>

      {mapCenter ? (
        <div className={styles.layout}>
          <div className={styles.mapContent}>
            <div
              className={styles.mapArea}
              style={{ height: ROUTE_MAP_HEIGHT }}
            >
              <MapContainer
                key={delivery._id}
                center={mapCenter}
                zoom={14}
                className={styles.mapContainer}
              >
                <MapController command={viewCommand} />
                <TileLayer url={TILE_LAYER_URL} />

                {route.length > 1 && (
                  <Polyline
                    positions={route}
                    pathOptions={{
                      color: color['color-action-fill'],
                      weight: ROUTE_LINE_WEIGHT,
                      opacity: ROUTE_LINE_OPACITY,
                      lineCap: 'round',
                      lineJoin: 'round',
                    }}
                  />
                )}

                {showIntentLine && liveDeliverymanLocation && intentTarget && (
                  <RouteLine from={liveDeliverymanLocation} to={intentTarget} />
                )}

                {liveDeliverymanLocation && (
                  <MapPin
                    position={liveDeliverymanLocation}
                    icon={deliverymanPointerIcon}
                    label={delivery.nomeEntregador}
                    isFocused={focusedTarget === 'deliveryman'}
                    popupRevision={popupRevision}
                    onClick={() => handlePinClick('deliveryman')}
                  />
                )}

                {showStart && startPosition && (
                  <RouteEndpointPin
                    kind="start"
                    position={startPosition}
                    label="Início do trajeto"
                    isFocused={focusedTarget === 'start'}
                    onClick={() => handlePinClick('start')}
                  />
                )}
                {deliveryLocation && (
                  <MapPin
                    position={deliveryLocation}
                    icon={DELIVERY_POINTER_ICON}
                    label="Entrega"
                    isFocused={focusedTarget === 'delivery'}
                    popupRevision={popupRevision}
                    onClick={() => handlePinClick('delivery')}
                  />
                )}
                {placeLocation && (
                  <MapPin
                    position={placeLocation}
                    icon={PLACE_POINTER_ICON}
                    label={delivery.nomeEstabelecimento || 'Loja'}
                    isFocused={focusedTarget === 'place'}
                    popupRevision={popupRevision}
                    onClick={() => handlePinClick('place')}
                  />
                )}
                {showEnd && endPosition && (
                  <RouteEndpointPin
                    kind="end"
                    position={endPosition}
                    label="Fim do trajeto"
                    isFocused={focusedTarget === 'end'}
                    onClick={() => handlePinClick('end')}
                  />
                )}
                {actionPins.map(pin => (
                  <RouteActionPin
                    key={pin.id}
                    index={pin.index}
                    position={pin.position}
                    label={pin.label}
                    time={pin.time}
                    executedByManager={pin.executedByManager}
                    isFocused={focusedTarget === pin.id}
                    onClick={() => handlePinClick(pin.id)}
                  />
                ))}
              </MapContainer>
              <MapOverlayChips
                onCenter={() => handleFocus('all')}
                unavailableMessage={unavailableMessage}
                isRetentionExpired={isRetentionExpired}
                deliveredMessage={
                  isDelivered ? { title: DELIVERED_STATUS_TITLE } : null
                }
              />
              <MapLegendChips
                focusedTarget={focusedTarget}
                availableTargets={availableTargets}
                hasRoute={route.length > 1}
                hasActions={actionPins.some(pin => !pin.executedByManager)}
                hasManagerActions={actionPins.some(
                  pin => pin.executedByManager,
                )}
                onFocus={handleFocus}
              />
            </div>
          </div>
          <aside className={styles.sidebar}>
            <RouteSequence
              items={sequence}
              focusedTarget={focusedTarget}
              onFocus={handleFocus}
              collapsible={collapsibleSequence}
              expandRevision={sequenceExpandRevision}
            />
          </aside>
        </div>
      ) : (
        <Text
          as="span"
          className={styles.unavailableLabel}
          fontSize="sm"
          tone="supporting"
        >
          {unavailableMessage
            ? `${unavailableMessage.title}. ${unavailableMessage.description}`
            : 'Rota não disponível'}
        </Text>
      )}
    </div>
  );
};
